"use client";

import React, { useState, useId } from "react";
import { ArrowRight, ChartBar as BarChart3, CircleCheck as CheckCircle2, Sparkles, ShieldCheck, TrendingUp, SlidersHorizontal as Sliders, Hotel } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RevparCalculator() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  const roomsId = useId();
  const adrId = useId();
  const occId = useId();

  // Interactive calculator state
  const [rooms, setRooms] = useState<number>(45);
  const [adr, setAdr] = useState<number>(3800);
  const [occupancy, setOccupancy] = useState<number>(62);

  // RevPAR Computations
  // Current RevPAR = ADR * (Occupancy / 100)
  const currentRevpar = Math.round(adr * (occupancy / 100));

  // Standard lift benchmark: 22% (within the 18-25% range)
  const liftFactor = 0.22;
  const projectedRevpar = Math.round(currentRevpar * (1 + liftFactor));
  const addedRevparPerDay = projectedRevpar - currentRevpar;

  // Estimated Annual Revenue Addition = Added RevPAR * Total Rooms * 365 days
  const annualAddition = Math.round(addedRevparPerDay * rooms * 365);
  const formatCurrency = (val: number) => {
    return "₹" + val.toLocaleString("en-IN");
  };
  return <section id="herorevparcalculator" data-nav="dark" ref={ref} className="relative overflow-hidden bg-[#090E1A] text-slate-100 py-20 md:py-28 lg:py-32">
      {/* Subtle Background Texture Grid */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-20 bg-[radial-gradient(#C1A357_1px,transparent_1px)] [background-size:24px_24px]" />
      <div aria-hidden="true" className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-[#C1A357]/10 blur-3xl pointer-events-none" />
      <div aria-hidden="true" className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-[#1E293B]/40 blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(16px)"
      }} className="transition-all duration-700 ease-out grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left Column: Hero Copy & Value Props */}
          <div className="lg:col-span-6 space-y-6">
            <div>
              <Badge variant="outline" className="border-[#C1A357]/40 bg-[#C1A357]/10 text-[#C1A357] px-3.5 py-1 text-xs font-semibold uppercase tracking-wider rounded-md inline-flex items-center gap-1.5 mb-5 shadow-xs">
                <Sparkles className="size-3.5 text-[#C1A357]" />
                Interactive RevPAR Calculator
              </Badge>

              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.15]">
                Estimate your hotel&apos;s revenue growth
              </h1>
            </div>

            <p className="text-slate-300 text-base sm:text-lg leading-relaxed max-w-xl">
              Enter your room count and current rates to calculate how much extra revenue data-backed OTA distribution and yield strategies can generate for your property.
            </p>

            {/* Feature Pills */}
            <div className="flex flex-wrap gap-2.5 pt-2">
              <span className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-[#141C2E] border border-slate-700/60 text-slate-200 text-xs sm:text-sm font-medium">
                <BarChart3 className="size-4 text-[#C1A357]" />
                Real-time OTA benchmarking
              </span>
              <span className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-[#141C2E] border border-slate-700/60 text-slate-200 text-xs sm:text-sm font-medium">
                <TrendingUp className="size-4 text-[#C1A357]" />
                Dynamic pricing yield modeling
              </span>
              <span className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-[#141C2E] border border-slate-700/60 text-slate-200 text-xs sm:text-sm font-medium">
                <CheckCircle2 className="size-4 text-[#C1A357]" />
                Proven direct conversion frameworks
              </span>
            </div>

            {/* Primary Action Button */}
            <div className="pt-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Button asChild size="lg" className="bg-[#C1A357] hover:bg-[#b09246] text-[#090E1A] font-semibold px-7 py-3 h-auto text-base rounded-md shadow-md transition-all hover:shadow-lg hover:scale-[1.01]">
                <Link to="#" className="inline-flex items-center gap-2">
                  Book a free revenue audit
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>

            {/* Trust Footnote */}
            <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-400 pt-2">
              <ShieldCheck className="size-4 text-[#C1A357] shrink-0" />
              <span>Tailored calculations for boutique, independent, and resort properties across India.</span>
            </div>
          </div>

          {/* Right Column: Live Interactive Calculator Widget */}
          <div className="lg:col-span-6 lg:pl-4">
            <div className="bg-[#101827] border border-slate-700/70 rounded-2xl p-6 sm:p-8 shadow-2xl relative overflow-hidden backdrop-blur-sm">
              {/* Top Accent Line */}
              <div aria-hidden="true" className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C1A357] to-transparent opacity-80" />

              {/* Card Header */}
              <div className="flex items-start justify-between border-b border-slate-800 pb-5 mb-6">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-lg bg-[#C1A357]/15 border border-[#C1A357]/30 flex items-center justify-center text-[#C1A357]">
                    <Sliders className="size-5" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white tracking-tight">
                      Quick RevPAR preview
                    </h2>
                    <p className="text-xs text-slate-400">
                      Based on standard 18–25% lift
                    </p>
                  </div>
                </div>
                <span className="px-2.5 py-1 text-xs font-bold rounded-md bg-[#C1A357]/20 text-[#C1A357] border border-[#C1A357]/40">
                  +22% Lift
                </span>
              </div>

              {/* Range Sliders Controls */}
              <div className="space-y-5 mb-8">
                {/* Control 1: Rooms */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <label htmlFor={roomsId} className="text-slate-300 font-medium flex items-center gap-1.5">
                      <Hotel className="size-3.5 text-slate-400" />
                      Inventory (Rooms)
                    </label>
                    <span className="text-[#C1A357] font-semibold text-sm bg-[#1A2337] px-2.5 py-0.5 rounded border border-slate-700">
                      {rooms} keys
                    </span>
                  </div>
                  <input id={roomsId} type="range" min={10} max={250} step={1} value={rooms} onChange={e => setRooms(Number(e.target.value))} className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-[#C1A357]" />
                  <div className="flex justify-between text-[11px] text-slate-500 font-mono">
                    <span>10 rooms</span>
                    <span>250 rooms</span>
                  </div>
                </div>

                {/* Control 2: Average Daily Rate (ADR) */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <label htmlFor={adrId} className="text-slate-300 font-medium">
                      Current Average Daily Rate (ADR)
                    </label>
                    <span className="text-[#C1A357] font-semibold text-sm bg-[#1A2337] px-2.5 py-0.5 rounded border border-slate-700">
                      {formatCurrency(adr)}
                    </span>
                  </div>
                  <input id={adrId} type="range" min={1000} max={20000} step={100} value={adr} onChange={e => setAdr(Number(e.target.value))} className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-[#C1A357]" />
                  <div className="flex justify-between text-[11px] text-slate-500 font-mono">
                    <span>₹1,000</span>
                    <span>₹20,000</span>
                  </div>
                </div>

                {/* Control 3: Average Occupancy */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <label htmlFor={occId} className="text-slate-300 font-medium">
                      Average Occupancy
                    </label>
                    <span className="text-[#C1A357] font-semibold text-sm bg-[#1A2337] px-2.5 py-0.5 rounded border border-slate-700">
                      {occupancy}%
                    </span>
                  </div>
                  <input id={occId} type="range" min={20} max={100} step={1} value={occupancy} onChange={e => setOccupancy(Number(e.target.value))} className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-[#C1A357]" />
                  <div className="flex justify-between text-[11px] text-slate-500 font-mono">
                    <span>20%</span>
                    <span>100%</span>
                  </div>
                </div>
              </div>

              {/* Computed Metric Outputs */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-[#151E30] border border-slate-800 rounded-xl p-3.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 block mb-1">
                    Current RevPAR
                  </span>
                  <span className="text-xl sm:text-2xl font-bold text-white">
                    {formatCurrency(currentRevpar)}
                  </span>
                </div>

                <div className="bg-[#151E30] border border-[#C1A357]/40 rounded-xl p-3.5 relative overflow-hidden">
                  <div aria-hidden="true" className="absolute top-0 right-0 size-8 bg-[#C1A357]/10 rounded-bl-xl" />
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-[#C1A357] block mb-1">
                    Projected RevPAR
                  </span>
                  <span className="text-xl sm:text-2xl font-bold text-[#C1A357]">
                    {formatCurrency(projectedRevpar)}
                  </span>
                </div>
              </div>

              {/* Total Estimated Growth Highlight Card */}
              <div className="bg-gradient-to-r from-[#C1A357] to-[#d6b763] rounded-xl p-4 sm:p-5 text-[#090E1A] shadow-lg mb-4 text-center">
                <span className="text-xs font-bold uppercase tracking-wider text-[#090E1A]/80 block mb-0.5">
                  Estimated Annual Revenue Addition
                </span>
                <div className="text-2xl sm:text-3xl font-extrabold text-[#090E1A] tracking-tight">
                  +{formatCurrency(annualAddition)}
                </div>
                <span className="text-[11px] font-medium text-[#090E1A]/80 block mt-1">
                  Calculated across {rooms} rooms over 365 operational days
                </span>
              </div>

              {/* Card Footer Consultation Link */}
              <div className="text-center pt-2">
                <Link to="#" className="text-xs sm:text-sm text-slate-300 hover:text-[#C1A357] underline underline-offset-4 font-medium transition-colors inline-flex items-center gap-1.5">
                  Get a custom audit breakdown for your property
                  <ArrowRight className="size-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}